import { Component, Inject, OnDestroy, OnInit, DOCUMENT } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService } from 'src/app/services/cloud.service';
import { NotificationService } from 'src/app/services/notification.service';
import { CalendlyService } from 'src/app/services/calendly.service';
import { environment } from 'src/environments/environment';

interface BlogCard {
  id: string;
  title: string;
  category: string;
  readTime: string;
  imageUrl: string;
  date: string;
  author: string;
  excerpt: string;
}

@Component({
  selector: 'app-blogs',
  imports: [RouterModule],
  templateUrl: './blogs.component.html',
  styleUrls: ['./blogs.component.css']
})
export class BlogsComponent implements OnInit, OnDestroy {

  public calendlyUrl = environment.CALENDLY_URL;
  blogPosts: BlogCard[] = [];
  loading = false;
  page = 1;
  limit = 20;
  totalCount = 0;
  private fetchSub?: Subscription;

  contactForm = new FormGroup({
    name: new FormControl('', Validators.required),
    email: new FormControl('', [Validators.required, Validators.email]),
    company: new FormControl(''),
    message: new FormControl('', Validators.required)
  });

  isSubmitting = false;
  submitted = false;

  constructor(
    @Inject(DOCUMENT) private doc: Document,
    private router: Router,
    private cloudService: CloudService,
    private notify: NotificationService,
    private calendly: CalendlyService
  ) { }

  ngOnInit(): void {
    this.loadBlogPosts();
  }

  ngOnDestroy(): void {
    this.fetchSub?.unsubscribe();
  }

  loadBlogPosts(): void {
    this.loading = true;
    this.fetchSub?.unsubscribe();

    this.fetchSub = this.cloudService.getAllBlogs(this.page, this.limit).subscribe({
      next: (data: any) => {
        const response = data?.object;
        const stories = response?.DATA?.stories;
        const source = Array.isArray(stories) ? stories : [];

        this.blogPosts = source.map((post: any, index: number) => this.mapBlogPost(post, index));

        const total = Number(response?.TOTAL_COUNT);
        const page = Number(response?.PAGE);
        const limit = Number(response?.LIMIT);

        this.totalCount = Number.isFinite(total) && total >= 0 ? total : this.blogPosts.length;
        this.page = Number.isFinite(page) && page > 0 ? page : this.page;
        this.limit = Number.isFinite(limit) && limit > 0 ? limit : this.limit;

        this.loading = false;
      },
      error: (e) => {
        this.blogPosts = [];
        this.totalCount = 0;
        this.loading = false;
        this.notify.showNotification(
          e?.error?.message,
          'top',
          'error',
          e?.error?.status
        );
      }
    });
  }

  mapBlogPost(post: any, index: number): BlogCard {
    const content = post?.content ?? {};
    const readTimeRaw = content?.read_time;
    const readTimeValue = Number(readTimeRaw);
    const readTime = Number.isFinite(readTimeValue) && readTimeValue > 0
      ? `${readTimeValue} min read`
      : `${5 + (index % 6)} min read`;

    const rawDate = content?.post_date || new Date().toISOString();
    const idValue = post?.slug || post?.uuid || post?.id || index + 1;

    return {
      id: String(idValue),
      title: content?.heading || 'Untitled Blog',
      excerpt: content?.short_description || 'No description available.',
      category: content?.category || 'General',
      readTime,
      imageUrl: content?.banner?.filename,
      date: this.formatDate(rawDate),
      author: content?.author_name || 'Cloud Atler Team'
    };
  }

  private formatDate(dateValue: string): string {
    const normalized = typeof dateValue === 'string' ? dateValue.replace(' ', 'T') : dateValue;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) {
      return dateValue;
    }
    return date.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' });
  }

  get totalPages(): number {
    if (!this.totalCount || !this.limit) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.totalCount / this.limit));
  }

  get startItem(): number {
    if (!this.totalCount) {
      return 0;
    }
    return (this.page - 1) * this.limit + 1;
  }

  get endItem(): number {
    if (!this.totalCount) {
      return 0;
    }
    return Math.min(this.page * this.limit, this.totalCount);
  }

  get paginationItems(): Array<number | '...'> {
    const total = this.totalPages;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: Array<number | '...'> = [1];
    let start = Math.max(2, this.page - 1);
    let end = Math.min(total - 1, this.page + 1);

    if (this.page <= 3) {
      start = 2;
      end = 4;
    } else if (this.page >= total - 2) {
      start = total - 3;
      end = total - 1;
    }

    if (start > 2) {
      pages.push('...');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < total - 1) {
      pages.push('...');
    }

    pages.push(total);
    return pages;
  }

  goToPage(targetPage: number): void {
    if (targetPage < 1 || targetPage > this.totalPages || targetPage === this.page) {
      return;
    }

    this.page = targetPage;
    this.loadBlogPosts();
    this.scrollToBlogTop();
  }

  goToPreviousPage(): void {
    if (this.page > 1) {
      this.goToPage(this.page - 1);
    }
  }

  goToNextPage(): void {
    if (this.page < this.totalPages) {
      this.goToPage(this.page + 1);
    }
  }

  private scrollToBlogTop(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const grid = this.doc.querySelector('.blogs-grid');
    if (!grid) {
      return;
    }

    const y = grid.getBoundingClientRect().top + window.scrollY - 120;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }

  openPost(postId: string): void {
    this.router.navigate(['/blog', postId]);
    this.scrollToTop();
  }

  openCalendlyPopup() {
    return this.calendly.openPopup(this.calendlyUrl);
  }

  onSubmit(): void {
    if (!this.contactForm.valid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    setTimeout(() => {
      this.isSubmitting = false;
      this.submitted = true;
      this.contactForm.reset();
      setTimeout(() => {
        this.submitted = false;
      }, 5000);
    }, 1500);
  }

  scrollToTop(): void {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }

}
